import type { Editor } from "@tiptap/react"

// Primitive UI Components
import { Button } from "src/tiptap/components/tiptap-ui-primitive/button"
import { Spacer } from "src/tiptap/components/tiptap-ui-primitive/spacer"
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuGroup,
  MenuGroupLabel,
  MenuButton,
} from "src/tiptap/components/tiptap-ui-primitive/menu"
import { ComboboxList } from "src/tiptap/components/tiptap-ui-primitive/combobox"
import { Separator } from "src/tiptap/components/tiptap-ui-primitive/separator"

// Tiptap UI
import {
  TEXT_COLORS,
  canColorText,
  useColorText,
} from "src/tiptap/components/tiptap-ui/color-text-button"
import {
  HIGHLIGHT_COLORS,
  canColorHighlight,
  useColorHighlight,
} from "src/tiptap/components/tiptap-ui/color-highlight-button"
import type { RecentColor } from "src/tiptap/components/tiptap-ui/color-text-popover"
import {
  getColorByValue,
  useRecentColors,
} from "src/tiptap/components/tiptap-ui/color-text-popover"

// Icons
import { PaintBucketIcon } from "src/tiptap/components/tiptap-icons/paint-bucket-icon"
import { ChevronRightIcon } from "src/tiptap/components/tiptap-icons/chevron-right-icon"
import { TextColorSmallIcon } from "src/tiptap/components/tiptap-icons/text-color-small-icon"
import { useTiptapEditor } from "src/tiptap/hooks/use-tiptap-editor"

interface ColorMenuItemProps {
  editor?: Editor | null
  color: { value: string; label: string }
}

const TextColorMenuItem: React.FC<ColorMenuItemProps> = ({ editor, color }) => {
  const { addRecentColor } = useRecentColors()
  const { isActive, handleColorText, label } = useColorText({
    editor,
    label: color.label,
    textColor: color.value,
    onApplied: ({ color, label }) =>
      addRecentColor({ type: "text", label, value: color }),
  })

  return (
    <MenuItem
      render={
        <Button variant="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={handleColorText}
    >
      <span className="tiptap-button-color-text" style={{ color: color.value }}>
        <TextColorSmallIcon
          className="tiptap-button-icon"
          style={{ color: color.value, flexGrow: 1 }}
        />
      </span>
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

const HighlightColorMenuItem: React.FC<ColorMenuItemProps> = ({
  editor,
  color,
}) => {
  const { addRecentColor } = useRecentColors()
  const { isActive, handleColorHighlight, label } = useColorHighlight({
    editor,
    label: color.label,
    highlightColor: color.value,
    mode: "node",
    onApplied: ({ color, label }) =>
      addRecentColor({ type: "highlight", label, value: color }),
  })

  return (
    <MenuItem
      render={
        <Button variant="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={handleColorHighlight}
    >
      <span
        className="tiptap-button-highlight"
        style={{ "--highlight-color": color.value } as React.CSSProperties}
      />
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  )
}

const RecentColorMenuItem: React.FC<{
  editor?: Editor | null
  colorObj: RecentColor
}> = ({ editor, colorObj }) => {
  const colorSet = colorObj.type === "text" ? TEXT_COLORS : HIGHLIGHT_COLORS
  const color = getColorByValue(colorObj.value, colorSet)

  const ColorComponent =
    colorObj.type === "text" ? TextColorMenuItem : HighlightColorMenuItem

  return <ColorComponent editor={editor} color={color} />
}

export interface ColorMenuProps {
  editor?: Editor | null
  /**
   * Custom trigger component. If not provided, uses default paint bucket button.
   */
  trigger?: React.ReactNode
  /**
   * Label for the color menu trigger
   * @default "Color"
   */
  label?: string
  /**
   * Menu placement relative to trigger
   * @default "right"
   */
  placement?: React.ComponentProps<typeof Menu>["placement"]
}

/**
 * Reusable color menu component that provides text and highlight color options.
 * Includes recent colors, text colors, and highlight colors sections.
 */
export const ColorMenu: React.FC<ColorMenuProps> = ({
  editor: providedEditor,
  trigger,
  label = "Color",
  placement = "right",
}) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { recentColors, isInitialized } = useRecentColors()

  const hasColorActions: boolean =
    canColorText(editor) ||
    canColorHighlight(editor, "mark") ||
    canColorHighlight(editor, "node")

  if (!editor || !hasColorActions) {
    return null
  }

  const defaultTrigger = (
    <MenuItem
      render={
        <MenuButton
          render={
            <Button variant="ghost">
              <PaintBucketIcon className="tiptap-button-icon" />
              <span className="tiptap-button-text">{label}</span>
              <Spacer />
              <ChevronRightIcon className="tiptap-button-icon" />
            </Button>
          }
        />
      }
    />
  )

  return (
    <Menu placement={placement} trigger={trigger || defaultTrigger}>
      <MenuContent portal>
        <ComboboxList>
          {/* Recent Colors */}
          {isInitialized && recentColors.length > 0 && (
            <MenuGroup>
              <MenuGroupLabel>Recent colors</MenuGroupLabel>
              {recentColors.map((colorObj) => (
                <RecentColorMenuItem
                  key={colorObj.value}
                  editor={editor}
                  colorObj={colorObj}
                />
              ))}
              <Separator orientation="horizontal" />
            </MenuGroup>
          )}

          {/* Text Colors */}
          <MenuGroup>
            <MenuGroupLabel>Text color</MenuGroupLabel>
            {TEXT_COLORS.map((textColor) => (
              <TextColorMenuItem
                key={textColor.value}
                editor={editor}
                color={textColor}
              />
            ))}
          </MenuGroup>

          <Separator orientation="horizontal" />

          {/* Background Colors */}
          <MenuGroup>
            <MenuGroupLabel>Background color</MenuGroupLabel>
            {HIGHLIGHT_COLORS.map((highlightColor) => (
              <HighlightColorMenuItem
                key={highlightColor.value}
                editor={editor}
                color={highlightColor}
              />
            ))}
          </MenuGroup>
        </ComboboxList>
      </MenuContent>
    </Menu>
  )
}

export { TextColorMenuItem, HighlightColorMenuItem, RecentColorMenuItem }
